import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fetchArtwork, fetchArtworkOrder, type Artwork, type ArtworkOrder } from '../api'
import { useAuth } from '../context/AuthContext'

export function ArtworkDetail() {
  const { id } = useParams<{ id: string }>()
  const { artist } = useAuth()
  const [artwork, setArtwork] = useState<Artwork | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState(false)
  const [buyerOrder, setBuyerOrder] = useState<ArtworkOrder | null>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    setBuyerOrder(null)
    fetchArtwork(id)
      .then(a => {
        if (a === null) { setNotFound(true); return }
        setArtwork(a)
        if (a.status === 'sold' && artist && artist.id === a.artistId) {
          fetchArtworkOrder(id)
            .then(order => setBuyerOrder(order))
            .catch(() => {})
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [id, artist])

  if (loading) return <p className="state-loading container">Laster inn...</p>

  if (notFound) {
    return (
      <div className="state-empty container">
        <p>Verk ikke funnet.</p>
        <Link to="/" style={{ marginTop: '1rem', display: 'inline-block', textDecoration: 'underline' }}>
          ← Tilbake til galleriet
        </Link>
      </div>
    )
  }

  if (error || !artwork) {
    return <p className="state-error container">Noe gikk galt.</p>
  }

  const isSold = artwork.status === 'sold'
  const isOwner = !!artist && artist.id === artwork.artistId
  const pageTitle = `${artwork.title} av ${artwork.artistName} — Originalene`
  const pageDesc = [
    artwork.medium,
    artwork.description ? artwork.description.slice(0, 120) : null,
    `${artwork.priceNok.toLocaleString('nb-NO')} NOK`,
  ].filter(Boolean).join('. ')
  const imageAbsUrl = `${window.location.origin}${artwork.imageUrl}`
  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: artwork.title,
    description: artwork.description ?? artwork.medium,
    image: imageAbsUrl,
    offers: {
      '@type': 'Offer',
      price: artwork.priceNok,
      priceCurrency: 'NOK',
      availability: isSold ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      seller: { '@type': 'Person', name: artwork.artistName },
    },
  })
  const dims =
    artwork.widthCm && artwork.heightCm
      ? `${artwork.widthCm} × ${artwork.heightCm} cm`
      : null

  return (
    <main className="artwork-detail container">
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={imageAbsUrl} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      <Link to="/" className="back-link">← Alle verk</Link>

      <div className="artwork-detail-layout">
        <div className="artwork-detail-img">
          {isSold && <div className="sold-overlay">Solgt</div>}
          <img src={artwork.imageUrl} alt={artwork.title} />
        </div>

        <div className="artwork-detail-info">
          <h1>{artwork.title}</h1>
          <p className="artwork-detail-artist">
            by{' '}
            <Link to={`/artist/${artwork.artistSlug}`}>{artwork.artistName}</Link>
          </p>

          <div className="artwork-meta">
            <div className="meta-row">
              <span className="meta-label">Medium</span>
              <span>{artwork.medium}</span>
            </div>
            {dims && (
              <div className="meta-row">
                <span className="meta-label">Mål</span>
                <span>{dims}</span>
              </div>
            )}
            <div className="meta-row">
              <span className="meta-label">Status</span>
              <span style={{ color: isSold ? 'var(--sold)' : undefined }}>
                {isSold ? 'Solgt' : 'Tilgjengelig'}
              </span>
            </div>
          </div>

          <p className="artwork-price">
            {artwork.priceNok.toLocaleString('nb-NO')} NOK
          </p>

          {artwork.description && (
            <p className="artwork-description">{artwork.description}</p>
          )}

          {isSold ? (
            <div className="btn-sold">Solgt</div>
          ) : (
            <Link to={`/checkout/${artwork.id}`} className="btn-buy">
              Kjøp dette verket
            </Link>
          )}

          {isSold && isOwner && buyerOrder && (
            <div className="order-contact-box">
              <p className="order-contact-title">Kjøperinformasjon</p>
              <div className="order-contact-row">
                <span className="order-contact-label">Navn</span>
                <span>{buyerOrder.buyerName}</span>
              </div>
              <div className="order-contact-row">
                <span className="order-contact-label">E-post</span>
                <a href={`mailto:${buyerOrder.buyerEmail}`} className="order-contact-link">
                  {buyerOrder.buyerEmail}
                </a>
              </div>
              {buyerOrder.buyerPhone && (
                <div className="order-contact-row">
                  <span className="order-contact-label">Telefon</span>
                  <a href={`tel:${buyerOrder.buyerPhone}`} className="order-contact-link">
                    {buyerOrder.buyerPhone}
                  </a>
                </div>
              )}
              <div className="order-contact-row">
                <span className="order-contact-label">Leveringsmåte</span>
                <span>{buyerOrder.shippingMethod === 'pickup' ? 'Henting' : 'Frakt'}</span>
              </div>
              {buyerOrder.buyerAddress && (
                <div className="order-contact-row">
                  <span className="order-contact-label">
                    {buyerOrder.shippingMethod === 'pickup' ? 'Henteadresse' : 'Leveringsadresse'}
                  </span>
                  <span>{buyerOrder.buyerAddress}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
